import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CustomerFormModal = ({ isOpen, onClose, onSubmit, editingCustomer }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    volume: '',
    paymentTerms: '',
    contractDuration: '',
    dependencyPercentage: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingCustomer) {
      setFormData(editingCustomer);
    } else {
      setFormData({
        name: '',
        type: '',
        volume: '',
        paymentTerms: '',
        contractDuration: '',
        dependencyPercentage: ''
      });
    }
    setErrors({});
  }, [editingCustomer, isOpen]);

  const customerTypes = [
    { value: 'Institusi Pemerintah', label: 'Institusi Pemerintah' },
    { value: 'Retail Modern', label: 'Retail Modern' },
    { value: 'Distributor', label: 'Distributor' },
    { value: 'Korporat Swasta', label: 'Korporat Swasta' },
    { value: 'UMKM', label: 'UMKM' }
  ];

  const paymentTermsOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Net 7', label: 'Net 7' },
    { value: 'Net 14', label: 'Net 14' },
    { value: 'Net 30', label: 'Net 30' },
    { value: 'Net 45', label: 'Net 45' },
    { value: 'Net 60', label: 'Net 60' }
  ];

  const contractDurationOptions = [
    { value: '3 bulan', label: '3 bulan' },
    { value: '6 bulan', label: '6 bulan' },
    { value: '1 tahun', label: '1 tahun' },
    { value: '2 tahun', label: '2 tahun' },
    { value: '3 tahun', label: '3 tahun' },
    { value: 'Tidak ada kontrak', label: 'Tidak ada kontrak' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) newErrors.name = 'Nama pelanggan wajib diisi';
    if (!formData?.type) newErrors.type = 'Tipe pelanggan wajib dipilih';
    if (!formData?.volume || formData?.volume <= 0) newErrors.volume = 'Volume harus lebih dari 0';
    if (!formData?.paymentTerms) newErrors.paymentTerms = 'Syarat pembayaran wajib dipilih';
    if (!formData?.contractDuration) newErrors.contractDuration = 'Durasi kontrak wajib dipilih';
    if (!formData?.dependencyPercentage || formData?.dependencyPercentage < 0 || formData?.dependencyPercentage > 100) {
      newErrors.dependencyPercentage = 'Persentase ketergantungan harus antara 0-100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        volume: parseFloat(formData?.volume),
        dependencyPercentage: parseFloat(formData?.dependencyPercentage)
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {editingCustomer ? 'Edit Pelanggan' : 'Tambah Pelanggan Baru'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          >
            <span className="sr-only">Close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Nama Pelanggan"
            type="text"
            placeholder="Contoh: PT Susu Sejahtera"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Select
            label="Tipe Pelanggan"
            options={customerTypes}
            value={formData?.type}
            onChange={(value) => handleChange('type', value)}
            error={errors?.type}
            placeholder="Pilih tipe pelanggan"
            required
          />

          <Input
            label="Volume Pembelian Bulanan (Liter)"
            type="number"
            placeholder="Contoh: 50000"
            value={formData?.volume}
            onChange={(e) => handleChange('volume', e?.target?.value)}
            error={errors?.volume}
            description="Rata-rata volume pembelian per bulan"
            required
          />

          <Select
            label="Syarat Pembayaran"
            options={paymentTermsOptions}
            value={formData?.paymentTerms}
            onChange={(value) => handleChange('paymentTerms', value)}
            error={errors?.paymentTerms}
            placeholder="Pilih syarat pembayaran"
            required
          />

          <Select
            label="Durasi Kontrak"
            options={contractDurationOptions}
            value={formData?.contractDuration}
            onChange={(value) => handleChange('contractDuration', value)}
            error={errors?.contractDuration}
            placeholder="Pilih durasi kontrak"
            required
          />

          <Input
            label="Persentase Ketergantungan (%)"
            type="number"
            placeholder="Contoh: 25"
            value={formData?.dependencyPercentage}
            onChange={(e) => handleChange('dependencyPercentage', e?.target?.value)}
            error={errors?.dependencyPercentage}
            description="Persentase kontribusi pelanggan terhadap total pendapatan"
            min="0"
            max="100"
            required
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Batal
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Save"
              iconPosition="left"
            >
              {editingCustomer ? 'Perbarui' : 'Simpan'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerFormModal;